import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';
import toast from 'react-hot-toast';

interface ItemHistoryEntry {
  id: number;
  transactionType: string;
  transactionDate: string;
  quantity: number;
  unit: string;
  supplierName?: string;
  customerName?: string;
  createdByName: string;
  tripCode?: string;
  notes?: string;
}

interface ItemHistoryData {
  warehouseId: number;
  warehouseName: string;
  itemId: number;
  itemName: string;
  totalIn: number;
  totalOut: number;
  currentStock: number;
  transactions: ItemHistoryEntry[];
}

interface ItemHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouseId: number;
  itemId: number;
  itemName: string;
  warehouseName: string;
}

export const ItemHistoryModal: React.FC<ItemHistoryModalProps> = ({
  isOpen,
  onClose,
  warehouseId,
  itemId,
  itemName,
  warehouseName,
}) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<ItemHistoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (isOpen && warehouseId && itemId) {
      loadHistory();
    }
  }, [isOpen, warehouseId, itemId, dateFrom, dateTo]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await warehouseAPI.getItemHistory(warehouseId, itemId, {
        dateFrom,
        dateTo,
      });
      setHistory(data);
    } catch (error) {
      toast.error(t('warehouse.itemHistory.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await warehouseAPI.downloadItemHistoryExport(warehouseId, itemId, {
        dateFrom,
        dateTo,
      });
      toast.success(t('warehouse.itemHistory.exportSuccess'));
    } catch (error) {
      toast.error(t('warehouse.itemHistory.exportError'));
    }
  };

  const formatTransactionType = (type: string) => {
    const colorMap: Record<string, string> = {
      TRIP_RECEIPT: 'bg-green-100 text-green-700',
      TRIP_ISSUE: 'bg-red-100 text-red-700',
      MANUAL_RECEIPT: 'bg-blue-100 text-blue-700',
      MANUAL_ISSUE: 'bg-orange-100 text-orange-700',
      ADJUSTMENT: 'bg-purple-100 text-purple-700',
      RECONCILIATION: 'bg-amber-100 text-amber-700',
    };
    return {
      text: t(`warehouse.itemHistory.transactionTypes.${type}`, { defaultValue: type }),
      color: colorMap[type] || 'bg-gray-100 text-gray-700',
    };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">
            {t('warehouse.itemHistory.title')}: {itemName} - {warehouseName}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm font-medium">{t('warehouse.itemHistory.fromDate')}:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-field ml-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('warehouse.itemHistory.toDate')}:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-field ml-2"
              />
            </div>
            <button onClick={handleExport} className="btn-secondary flex items-center ml-auto">
              <Download className="w-4 h-4 mr-2" />
              {t('warehouse.itemHistory.exportExcel')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-10">{t('common.loading')}</div>
          ) : !history ? (
            <div className="text-center py-10 text-gray-500">{t('warehouse.itemHistory.noData')}</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.date')}</th>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.type')}</th>
                    <th className="p-2 text-right">{t('warehouse.itemHistory.quantity')}</th>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.supplierCustomer')}</th>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.operator')}</th>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.trip')}</th>
                    <th className="p-2 text-left">{t('warehouse.itemHistory.notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.transactions.map((tx) => {
                    const typeInfo = formatTransactionType(tx.transactionType);
                    return (
                      <tr key={tx.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {new Date(tx.transactionDate).toLocaleDateString('vi-VN')}
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${typeInfo.color}`}>
                            {typeInfo.text}
                          </span>
                        </td>
                        <td className="p-2 text-right font-medium">
                          {tx.quantity} {tx.unit.toLowerCase()}
                        </td>
                        <td className="p-2">{tx.supplierName || tx.customerName || '-'}</td>
                        <td className="p-2">{tx.createdByName}</td>
                        <td className="p-2">{tx.tripCode || '-'}</td>
                        <td className="p-2 text-gray-600">{tx.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer Summary */}
        {history && (
          <div className="p-4 border-t bg-yellow-50">
            <div className="flex justify-around text-center">
              <div>
                <div className="text-sm text-gray-600">{t('warehouse.itemHistory.totalIn')}</div>
                <div className="text-lg font-bold text-green-600">{history.totalIn}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t('warehouse.itemHistory.totalOut')}</div>
                <div className="text-lg font-bold text-red-600">{history.totalOut}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t('warehouse.itemHistory.balance')}</div>
                <div className="text-lg font-bold text-blue-600">{history.currentStock}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
